<template>
  <div class="home-page">
    <div id="particles-js"></div>

    <!-- Hero -->
    <section class="hero-section d-flex flex-column justify-center align-center text-center">
      <p class="welcome-label">Welcome to</p>
      <h1 class="brand-name">
        <span class="g-wrap"><span class="g-char">{{ websiteName.charAt(0) }}</span><img class="g-img" :src="logoGlyphea" alt="" aria-hidden="true" /></span>{{ websiteName.slice(1) }}
      </h1>
      <p class="hero-subtitle">Transcribe and annotate historical documents with ease</p>
    </section>

    <div class="glow-divider"></div>

    <!-- Features -->
    <section class="content-section">
      <v-container>
        <v-row justify="center">
          <v-col cols="4">
            <div class="feature-card">
              <div class="feature-icon-wrap">
                <v-icon size="34">mdi-bookmark-outline</v-icon>
              </div>
              <h3 class="feature-title">Document Annotation</h3>
              <p class="feature-text">Mark and annotate historical manuscripts effectively with powerful tools.</p>
            </div>
          </v-col>
          <v-col cols="4">
            <div class="feature-card">
              <div class="feature-icon-wrap">
                <v-icon size="34">mdi-database-outline</v-icon>
              </div>
              <h3 class="feature-title">Glyph Database</h3>
              <p class="feature-text">Centralized glyph storage ensures consistent transcription across projects.</p>
            </div>
          </v-col>
          <v-col cols="4">
            <div class="feature-card">
              <div class="feature-icon-wrap">
                <v-icon size="34">mdi-file-export-outline</v-icon>
              </div>
              <h3 class="feature-title">Export Tools</h3>
              <p class="feature-text">Export annotated documents and virtual keyboards in multiple formats.</p>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <div class="glow-divider"></div>

    <!-- Carousel -->
    <section class="content-section text-center">
      <v-container>
        <h2 class="section-title mb-10">Explore Historical Documents</h2>
        <swiper
          :modules="swiperModules"
          :slides-per-view="1"
          navigation
          :pagination="{ clickable: true }"
          :autoplay="{ delay: 3000, disableOnInteraction: false }"
          class="doc-swiper"
        >
          <swiper-slide>
            <v-img :src="historicalDocument1" alt="Historical Document 1" height="340px" cover class="slide-img" />
          </swiper-slide>
          <swiper-slide>
            <v-img :src="historicalDocument2" alt="Historical Document 2" height="340px" cover class="slide-img" />
          </swiper-slide>
          <swiper-slide>
            <v-img :src="historicalDocument3" alt="Historical Document 3" height="340px" cover class="slide-img" />
          </swiper-slide>
        </swiper>
      </v-container>
    </section>

    <div class="glow-divider"></div>

    <!-- Bottom CTA -->
    <section class="content-section text-center pb-12">
      <h2 class="cta-footer-title">Ready to Transcribe?</h2>
      <p class="cta-footer-sub">Begin your journey of historical transcription today.</p>
    </section>
  </div>
</template>

<script>
import "particles.js";
import { Swiper, SwiperSlide } from "swiper/vue";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import historicalDocument1 from '@/assets/historical-document-1.jpg';
import historicalDocument2 from '@/assets/historical-document-2.jpg';
import historicalDocument3 from '@/assets/historical-document-3.jpg';
import logoGlyphea from '@/assets/G_shaped_logo.png';

export default {
  name: "HomePage",
  components: { Swiper, SwiperSlide },
  data() {
    return {
      websiteName: process.env.VUE_APP_WEBSITE_NAME,
      logoGlyphea,
      historicalDocument1,
      historicalDocument2,
      historicalDocument3,
      swiperModules: [Autoplay, Navigation, Pagination],
    };
  },
  mounted() {
    window.particlesJS("particles-js", {
      particles: {
        number: { value: 80, density: { enable: true, value_area: 800 } },
        size: { value: 3, random: true },
        move: { enable: true, speed: 2 },
        opacity: { value: 0.45 },
        shape: { type: "circle" },
        line_linked: { enable: true, distance: 150, color: "#b0b0b0", opacity: 0.4, width: 1 },
        color: { value: "#4a4a4a" },
      },
      interactivity: {
        detect_on: "canvas",
        events: {
          onhover: { enable: true, mode: "grab" },
          onclick: { enable: true, mode: "push" },
        },
        modes: {
          grab: { distance: 140, line_linked: { opacity: 0.55 } },
          push: { particles_nb: 3 },
        },
      },
      retina_detect: true,
    });
  },
};
</script>

<style scoped>
/* ── Layout ── */
.home-page {
  position: relative;
  overflow-x: hidden;
}

#particles-js {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
}

.hero-section,
.content-section {
  position: relative;
  z-index: 1;
}

.hero-section {
  padding: 3rem 1.5rem 1.5rem;
}

.content-section {
  max-width: 1100px;
  margin: 0 auto;
  padding: 1rem 1rem 2rem;
}

/* ── Divider ── */
.glow-divider {
  position: relative;
  z-index: 1;
  width: 55%;
  max-width: 520px;
  height: 1px;
  margin: 2.5rem auto;
  background: linear-gradient(
    to right,
    transparent,
    rgba(130, 177, 255, 0.55),
    rgba(179, 157, 219, 0.45),
    transparent
  );
}

/* ── Hero text ── */
.welcome-label {
  font-size: 0.95rem;
  font-weight: 500;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: rgba(var(--v-theme-on-background), 0.5);
  margin-bottom: 0.4rem;
}

.brand-name {
  font-size: clamp(3.5rem, 10vw, 6.5rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.15;
  padding-bottom: 0.12em;
  background: linear-gradient(
    135deg,
    rgb(var(--v-theme-on-background)) 20%,
    #82b1ff 55%,
    #b39ddb 85%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: brand-glow 3.2s ease-in-out infinite;
}

@keyframes brand-glow {
  0%, 100% {
    filter: drop-shadow(0 0 10px rgba(130, 177, 255, 0.5))
            drop-shadow(0 0 22px rgba(130, 177, 255, 0.2));
  }
  50% {
    filter: drop-shadow(0 0 20px rgba(130, 177, 255, 0.92))
            drop-shadow(0 0 45px rgba(179, 157, 219, 0.65))
            drop-shadow(0 0 72px rgba(130, 177, 255, 0.3));
  }
}

/* ── G ↔ logo swap ── */
.g-wrap {
  position: relative;
  display: inline-block;
  isolation: isolate;
}
.g-char {
  display: inline-block;
  animation: g-char-fade 8s ease-in-out infinite;
}
.g-img {
  position: absolute;
  top: -12%;
  left: -12%;
  width: 124%;
  height: 124%;
  object-fit: contain;
  opacity: 0;
  animation: g-logo-fade 8s ease-in-out infinite;
}

@keyframes g-char-fade {
  0%, 22%  { opacity: 1; }
  35%, 65% { opacity: 0; }
  78%, 100% { opacity: 1; }
}
@keyframes g-logo-fade {
  0%, 22%  { opacity: 0; }
  35%, 65% { opacity: 1; }
  78%, 100% { opacity: 0; }
}

.hero-subtitle {
  font-size: 1.25rem;
  font-weight: 400;
  color: rgba(var(--v-theme-on-background), 0.65);
  max-width: 480px;
  line-height: 1.65;
  margin-top: 1rem;
}

/* ── Section titles ── */
.section-title {
  font-size: 1.9rem;
  font-weight: 700;
  color: rgb(var(--v-theme-on-background));
  display: inline-block;
  position: relative;
}
.section-title::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 55%;
  height: 2px;
  border-radius: 2px;
  background: linear-gradient(to right, transparent, rgba(130, 177, 255, 0.6), transparent);
}

/* ── Feature cards ── */
.feature-card {
  padding: 2.2rem 1.8rem;
  border-radius: 20px;
  background: rgba(var(--v-theme-surface-variant), 0.55);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(130, 177, 255, 0.14);
  text-align: center;
  height: 100%;
  transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1),
              box-shadow 0.35s ease,
              border-color 0.35s ease;
}
.feature-card:hover {
  transform: translateY(-10px) scale(1.01);
  box-shadow: 0 18px 44px rgba(130, 177, 255, 0.18), 0 0 0 1px rgba(130, 177, 255, 0.22);
  border-color: rgba(130, 177, 255, 0.3);
}

.feature-icon-wrap {
  width: 68px;
  height: 68px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(130, 177, 255, 0.12), rgba(179, 157, 219, 0.12));
  border: 1px solid rgba(130, 177, 255, 0.28);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.4rem;
  transition: box-shadow 0.35s ease;
}
.feature-card:hover .feature-icon-wrap {
  box-shadow: 0 0 22px rgba(130, 177, 255, 0.38);
}

.feature-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: rgb(var(--v-theme-on-background));
  margin-bottom: 0.6rem;
}
.feature-text {
  font-size: 0.93rem;
  color: rgba(var(--v-theme-on-background), 0.65);
  line-height: 1.6;
  margin: 0;
}

/* ── Carousel ── */
.doc-swiper {
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.18), 0 0 0 1px rgba(130, 177, 255, 0.1);
}
.slide-img {
  border-radius: 0;
}

/* ── Bottom CTA ── */
.cta-footer-title {
  font-size: 2.2rem;
  font-weight: 700;
  color: rgb(var(--v-theme-on-background));
}
.cta-footer-sub {
  font-size: 1.05rem;
  color: rgba(var(--v-theme-on-background), 0.6);
  margin-top: 0.5rem;
}
</style>
